export function escapeAttribute(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function getFieldInputId(name) {
  return `cat-react-field-${name}`;
}

export function buildInitialValues(sections) {
  const values = {};

  const visitField = (field) => {
    if (field.component === 'dependant') {
      Object.values(field.variants || {}).forEach((variant) => {
        if (variant) {
          visitField(variant);
        }
      });
      return;
    }

    if (field.component === 'dependant_group') {
      Object.values(field.variants || {}).forEach((variantFields) => {
        (variantFields || []).forEach((variantField) => visitField(variantField));
      });
      return;
    }

    if (!(field.name in values)) {
      values[field.name] = field.defaultValue ?? (field.component === 'checkbox' ? false : '');
    }
  };

  (sections || []).forEach((section) => {
    (section.fields || []).forEach((field) => visitField(field));
  });

  return values;
}

export function collectFieldNames(field, names = []) {
  if (!field) {
    return names;
  }

  if (field.component === 'dependant') {
    Object.values(field.variants || {}).forEach((variant) => collectFieldNames(variant, names));
    return names;
  }

  if (field.component === 'dependant_group') {
    Object.values(field.variants || {}).forEach((variantFields) => {
      (variantFields || []).forEach((variantField) => collectFieldNames(variantField, names));
    });
    return names;
  }

  if (field.name) {
    names.push(field.name);
  }

  return names;
}

export function findFirstErrorFieldName(errors, sections) {
  const errorNames = new Set(Object.keys(errors || {}));
  if (errorNames.size === 0) {
    return '';
  }

  for (const section of sections || []) {
    for (const field of section.fields || []) {
      for (const fieldName of collectFieldNames(field, [])) {
        if (errorNames.has(fieldName)) {
          return fieldName;
        }
      }
    }
  }

  return Object.keys(errors || {})[0] || '';
}

export function findStepIndexForField(fieldName, sections) {
  if (!fieldName) {
    return null;
  }

  for (let index = 0; index < (sections || []).length; index += 1) {
    const section = sections[index];
    const fieldNames = [];
    (section.fields || []).forEach((field) => collectFieldNames(field, fieldNames));
    if (fieldNames.includes(fieldName)) {
      return index;
    }
  }

  return null;
}

export function normalizeMessages(messageList, fallbackMessage = '', fallbackType = 'error') {
  if (Array.isArray(messageList) && messageList.length > 0) {
    return messageList
      .map((message, index) => {
        if (typeof message === 'string') {
          return {
            id: `message-${index}`,
            type: fallbackType,
            text: message,
          };
        }

        if (!message || typeof message !== 'object') {
          return null;
        }

        const text = String(message.text || message.message || '').trim();
        if (!text) {
          return null;
        }

        return {
          id: String(message.id || `message-${index}`),
          type: String(message.type || fallbackType),
          text,
        };
      })
      .filter(Boolean);
  }

  if (!fallbackMessage) {
    return [];
  }

  return [{
    id: 'message-fallback',
    type: fallbackType,
    text: String(fallbackMessage),
  }];
}

export async function readApiPayload(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      return {};
    }
  }

  const text = await response.text();
  return {
    ok: response.ok,
    message: text.trim(),
    messages: text.trim() ? [{ type: response.ok ? 'status' : 'error', text: text.trim() }] : [],
  };
}

export function mergeOptions(existingOptions, incomingOptions) {
  const map = new Map();
  [...(existingOptions || []), ...(incomingOptions || [])].forEach((option) => {
    map.set(String(option.value), option);
  });
  return Array.from(map.values());
}

export function readJsonScript(element, selector) {
  const script = element ? element.querySelector(selector) : null;
  if (!script) {
    return null;
  }

  try {
    return JSON.parse(script.textContent || '{}');
  } catch (error) {
    console.error('[cst_admin_tools] invalid json payload', error);
    return null;
  }
}

export function deriveSchemaUrlFromReactPageUrl(pageUrl) {
  if (!pageUrl || typeof window === 'undefined') {
    return '';
  }

  try {
    const url = new URL(pageUrl, window.location.origin);
    const marker = '/admin/cst-admin-tools/react/';
    const markerIndex = url.pathname.indexOf(marker);

    if (markerIndex === -1) {
      return '';
    }

    const basePath = url.pathname.slice(0, markerIndex);
    const routeName = url.pathname.slice(markerIndex + marker.length);
    if (!routeName) {
      return '';
    }

    url.pathname = `${basePath}/admin/cst-admin-tools/react-api/${routeName}/schema`;
    return url.toString();
  } catch (error) {
    console.warn('[cst_admin_tools] unable to derive schema URL from React page URL', pageUrl, error);
    return '';
  }
}

export function buildClassicSidebarIframeUrl(url) {
  if (!url || typeof window === 'undefined') {
    return '';
  }

  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('cst_sidebar_iframe', '1');
    return nextUrl.toString();
  } catch (error) {
    console.warn('[cst_admin_tools] unable to build sidebar iframe URL', url, error);
    return url;
  }
}

export function isRenderableSchemaPayload(data) {
  return Boolean(
    data
    && typeof data === 'object'
    && Array.isArray(data.sections)
  );
}

export function getSidebarConfigForAction(action) {
  if (!action) {
    return {
      schemaUrl: '',
      pageUrl: '',
      fallbackUrl: '',
    };
  }

  if (action.kind === 'edit') {
    return {
      schemaUrl: '',
      pageUrl: action.url || '',
      fallbackUrl: action.url || '',
    };
  }

  return {
    schemaUrl: action.reactSchemaUrl || '',
    pageUrl: action.reactPageUrl || '',
    fallbackUrl: action.url || '',
  };
}

export function normalizeSearchableText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateFields(sections, values) {
  const errors = {};

  const visitField = (field) => {
    if (field.component === 'dependant') {
      Object.values(field.variants || {}).forEach((variant) => {
        if (variant) visitField(variant);
      });
      return;
    }

    if (field.component === 'dependant_group') {
      Object.values(field.variants || {}).forEach((variantFields) => {
        (variantFields || []).forEach((variantField) => visitField(variantField));
      });
      return;
    }

    if (field.component === 'email' && values[field.name]) {
      if (!EMAIL_REGEX.test(values[field.name])) {
        errors[field.name] = 'Adresse email invalide.';
      }
    }
  };

  (sections || []).forEach((section) => {
    (section.fields || []).forEach((field) => visitField(field));
  });

  return errors;
}
